// src/features/auth/components/AuditLog.tsx
import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon, 
  FunnelIcon, 
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import authService from '../services/authService';
import { AuthEvent, AuthEventType } from '../types';

interface AuditLogProps {
  userId?: string | number;
  onClose?: () => void;
}

const AuditLog: React.FC<AuditLogProps> = ({ userId, onClose }) => {
  const [events, setEvents] = useState<AuthEvent[]>([]);
  const [eventTypes, setEventTypes] = useState<AuthEventType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedEventType, setSelectedEventType] = useState<string>('all');
  const [showSuccessOnly, setShowSuccessOnly] = useState(false);

  useEffect(() => {
    loadData();
  }, [userId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [eventsResponse, eventTypesResponse] = await Promise.allSettled([
        userId ? authService.getUserAuthEvents(userId) : authService.getAuthEvents(),
        authService.getAuthEventTypes()
      ]);

      if (eventsResponse.status === 'fulfilled') {
        setEvents(eventsResponse.value.events);
      } else {
        setError('Failed to load events');
      }

      if (eventTypesResponse.status === 'fulfilled') {
        setEventTypes(eventTypesResponse.value.event_types);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load audit data');
    } finally {
      setLoading(false);
    }
  };

  const getEventIcon = (eventType: string, success: boolean) => {
    if (success) {
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    } else {
      return <XCircleIcon className="h-5 w-5 text-red-500" />;
    }
  };

  const getEventTypeColor = (eventType: string) => {
    switch (eventType.toLowerCase()) {
      case 'login':
        return 'text-blue-600 dark:text-blue-400';
      case 'logout':
        return 'text-gray-600 dark:text-gray-400';
      case 'register':
        return 'text-green-600 dark:text-green-400';
      case 'password_change':
        return 'text-yellow-600 dark:text-yellow-400';
      case '2fa_enable':
      case '2fa_disable':
        return 'text-purple-600 dark:text-purple-400';
      case 'failed_login':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-theme-text dark:text-dark-theme-text';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const filteredEvents = events.filter(event => {
    if (selectedEventType !== 'all' && event.event_type !== selectedEventType) {
      return false;
    }
    if (showSuccessOnly && !event.success) {
      return false;
    }
    return true;
  });

  return (
    <div className="max-w-6xl mx-auto bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary rounded-lg shadow-xl p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-3">
          <ChartBarIcon className="h-6 w-6 text-primary-light dark:text-primary-dark" />
          <h2 className="text-xl font-semibold text-theme-text dark:text-dark-theme-text">
            {userId ? 'User Audit Log' : 'Authentication Audit Log'}
          </h2>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text"
          >
            ✕
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <p className="ml-2 text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-center gap-4 p-4 bg-theme-bg dark:bg-dark-theme-bg rounded-lg border border-theme-border dark:border-dark-theme-border">
        <div className="flex items-center space-x-2">
          <FunnelIcon className="h-5 w-5 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
          <span className="text-sm font-medium text-theme-text dark:text-dark-theme-text">Filters:</span>
        </div>
        
        <select
          id="event-type-filter"
          value={selectedEventType}
          onChange={(e) => setSelectedEventType(e.target.value)}
          className="px-3 py-1 border border-theme-border dark:border-dark-theme-border rounded-md text-sm bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text"
          aria-label="Filter by event type"
          title="Select event type to filter audit log entries"
        >
          <option value="all">All Event Types</option>
          {eventTypes.map((type) => (
            <option key={type.code} value={type.code}>
              {type.name}
            </option>
          ))}
        </select>

        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={showSuccessOnly}
            onChange={(e) => setShowSuccessOnly(e.target.checked)}
            className="rounded border-theme-border dark:border-dark-theme-border text-primary-light dark:text-primary-dark focus:ring-primary-light dark:focus:ring-primary-dark"
          />
          <span className="text-sm text-theme-text dark:text-dark-theme-text">Success only</span>
        </label>

        <button
          onClick={loadData}
          disabled={loading}
          className="flex items-center space-x-2 px-3 py-1 border border-theme-border dark:border-dark-theme-border rounded-md text-sm font-medium text-theme-text dark:text-dark-theme-text hover:bg-theme-bg-secondary dark:hover:bg-dark-theme-bg-secondary disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Events List */}
      {loading && events.length === 0 ? (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-light"></div>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredEvents.length === 0 ? (
            <div className="text-center py-8">
              <InformationCircleIcon className="mx-auto h-12 w-12 text-theme-text-tertiary dark:text-dark-theme-text-tertiary" />
              <p className="mt-2 text-theme-text-secondary dark:text-dark-theme-text-secondary">
                No events found matching the current filters
              </p>
            </div>
          ) : (
            filteredEvents.map((event) => (
              <div
                key={event.id}
                className="bg-theme-bg dark:bg-dark-theme-bg rounded-lg p-4 border border-theme-border dark:border-dark-theme-border"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    {getEventIcon(event.event_type, event.success)}
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className={`font-medium ${getEventTypeColor(event.event_type)}`}>
                          {eventTypes.find(t => t.code === event.event_type)?.name || event.event_type}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          event.success 
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                            : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                        }`}>
                          {event.success ? 'Success' : 'Failed'}
                        </span>
                      </div>
                      
                      <p className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary mt-1">
                        {formatDate(event.timestamp)}
                      </p>
                      
                      <div className="mt-2 text-xs text-theme-text-tertiary dark:text-dark-theme-text-tertiary space-y-1">
                        <div>IP: {event.ip_address}</div>
                        {event.location && <div>Location: {event.location}</div>}
                        <div>User Agent: {event.user_agent}</div>
                        {userId && <div>User ID: {event.user_id}</div>}
                      </div>

                      {event.details && Object.keys(event.details).length > 0 && (
                        <div className="mt-3 p-2 bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary rounded border">
                          <details>
                            <summary className="text-xs font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary cursor-pointer">
                              Event Details
                            </summary>
                            <pre className="mt-2 text-xs text-theme-text-tertiary dark:text-dark-theme-text-tertiary overflow-x-auto">
                              {JSON.stringify(event.details, null, 2)}
                            </pre>
                          </details>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Summary */}
      <div className="mt-6 pt-4 border-t border-theme-border dark:border-dark-theme-border">
        <div className="flex justify-between items-center text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">
          <span>
            Showing {filteredEvents.length} of {events.length} events
          </span>
          <span>
            Success rate: {events.length > 0 ? Math.round((events.filter(e => e.success).length / events.length) * 100) : 0}%
          </span>
        </div>
      </div>
    </div>
  );
};

export default AuditLog;
